import {
  type Box,
  P,
  SLOPE,
  groundElbow,
  lerp,
  leftV,
  rightV,
  bottomV,
  topV,
  type Pt,
} from '@/components/iso/iso';
import type { IsoConnector, IsoMotion, IsoObjectSpec } from '@/components/iso/IsoSceneStage';
import { DIM, INK, MONO, MUTED, ROSE, RouterPlate, Slab } from '@/components/iso/parts';

/** The sealed boundary — the /security hero's cast. Slot geometry reuses the
 *  proven developers-hero layout (plate center, satellites orbiting), with
 *  the bottom-left slots retired and the DB tile centered below. */
const S = {
  plate: { tx: 583, ty: 210, w: 340, h: 300, depth: 26 },
  lock: { tx: 310, ty: 56, w: 168, h: 96, depth: 10 }, // top-left, drops in
  shield: { tx: 880, ty: 60, w: 190, h: 110, depth: 12 }, // top-right, drops in
  ledger: { tx: 1005, ty: 240, w: 140, h: 130, depth: 12 }, // right
  pin: { tx: 90, ty: 296, w: 120, h: 90, depth: 10 }, // left
  tenantdb: { tx: 560, ty: 640, w: 190, h: 120, depth: 12 }, // bottom center
} satisfies Record<string, Box>;

/* ---- Scene-local parts (flat 2D in each box's local space). ---- */

function LockTile() {
  const box = S.lock;
  return (
    <Slab box={box} topFill="#ffffff" rx={9}>
      {/* Padlock: shackle arc + body. */}
      <path d="M 26 40 v -8 a 10 10 0 0 1 20 0 v 8" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
      <rect x="20" y="40" width="32" height="26" rx="6" fill={INK} />
      <circle cx="36" cy="52" r="3.5" fill={ROSE} />
      <text x="64" y="46" fill={INK} fontSize="15" fontWeight="700" style={{ fontFamily: MONO }}>
        TLS 1.2+
      </text>
      <text x="64" y="64" fill={DIM} fontSize="11" style={{ fontFamily: MONO }}>
        1.3 preferred
      </text>
    </Slab>
  );
}

function ShieldTile() {
  const box = S.shield;
  return (
    <Slab box={box} topFill="#ffffff" rx={9}>
      {/* Shield with a check. */}
      <path d="M 40 18 l 20 8 v 18 c 0 14 -9 24 -20 29 c -11 -5 -20 -15 -20 -29 v -18 Z" fill={INK} />
      <path d="M 31 46 l 6 6 l 12 -13" fill="none" stroke={ROSE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <text x="74" y="42" fill={INK} fontSize="14" fontWeight="700">
        Abuse checks
      </text>
      <text x="74" y="60" fill={MUTED} fontSize="11" style={{ fontFamily: MONO }}>
        honeypot · pow
      </text>
      <rect x="74" y="72" width="86" height="7" rx="3.5" fill="#f2f1f5" />
    </Slab>
  );
}

function LedgerTile() {
  const box = S.ledger;
  const rows = [22, 46, 70];
  return (
    <Slab box={box} topFill="#ffffff" rx={9}>
      {rows.map((y) => (
        <g key={y}>
          <circle cx="20" cy={y + 4} r="3" fill={ROSE} />
          <rect x="32" y={y} width="66" height="8" rx="4" fill="#ecebf0" />
          <rect x="102" y={y} width="22" height="8" rx="4" fill="#f2f1f5" />
        </g>
      ))}
      <text x="16" y="112" fill={DIM} fontSize="10.5" style={{ fontFamily: MONO }}>
        append-only
      </text>
    </Slab>
  );
}

function PinTile() {
  const box = S.pin;
  return (
    <Slab box={box} topFill="#ffffff" rx={9}>
      {/* Truncation glyph — deliberately not a map pin: no location is claimed. */}
      <rect x="20" y="22" width="28" height="24" rx="6" fill={ROSE} />
      <rect x="26" y="32" width="16" height="4" rx="2" fill="#ffffff" />
      <text x="58" y="34" fill={INK} fontSize="13" fontWeight="700" style={{ fontFamily: MONO }}>
        IP /24
      </text>
      <text x="58" y="50" fill={DIM} fontSize="10" style={{ fontFamily: MONO }}>
        before storage
      </text>
    </Slab>
  );
}

function TenantDbTile() {
  const box = S.tenantdb;
  return (
    <Slab box={box} topFill="#ffffff" rx={9}>
      {/* Two tenant compartments split by the boundary wall. */}
      <rect x="14" y="14" width="72" height="58" rx="7" fill="#f7f7fa" stroke="rgba(16,16,28,0.14)" strokeWidth="1" />
      <rect x="104" y="14" width="72" height="58" rx="7" fill="#f7f7fa" stroke="rgba(16,16,28,0.14)" strokeWidth="1" />
      <line x1="95" y1="8" x2="95" y2="78" stroke={ROSE} strokeWidth="2.5" strokeDasharray="5 4" />
      <text x="50" y="38" fill={MUTED} fontSize="10.5" textAnchor="middle" style={{ fontFamily: MONO }}>
        tenant a
      </text>
      <text x="140" y="38" fill={MUTED} fontSize="10.5" textAnchor="middle" style={{ fontFamily: MONO }}>
        tenant b
      </text>
      <rect x="24" y="48" width="52" height="6" rx="3" fill="#ecebf0" />
      <rect x="114" y="48" width="52" height="6" rx="3" fill="#ecebf0" />
      <text x="14" y="100" fill={INK} fontSize="11.5" style={{ fontFamily: MONO }}>
        WHERE <tspan fill={ROSE}>tenant_id = $ctx</tspan>
      </text>
    </Slab>
  );
}

/* ---- Connectors: every path is sealed into the core. ---- */

const CONNECTORS: IsoConnector[] = (() => {
  const { plate, lock, shield, ledger, pin, tenantdb } = S;

  // lock card → plate face: vertical drop, then diagonal down-right
  // (same construction as the old request connector).
  const lockA: Pt = { x: bottomV(lock).x, y: bottomV(lock).y + lock.depth + 4 };
  const lockB = lerp(topV(plate), leftV(plate), 0.5);
  const lockMid: Pt = { x: lockA.x, y: lockB.y - SLOPE * Math.abs(lockB.x - lockA.x) };
  const dLock = `M ${P(lockA)} L ${P(lockMid)} L ${P(lockB)}`;

  // plate → shield: diagonal up-right, then riser into the shield's bottom
  // (same construction as the old browser connector).
  const shEnd: Pt = { x: bottomV(shield).x, y: bottomV(shield).y + shield.depth + 4 };
  const shDrop: Pt = { x: shEnd.x, y: shEnd.y + 13 };
  const shStart: Pt = { x: 790, y: shDrop.y + SLOPE * (shDrop.x - 790) };
  const dShield = `M ${P(shStart)} L ${P(shDrop)} L ${P(shEnd)}`;

  return [
    { id: 'sec-lock', d: dLock, node: lockA, pulse: true },
    { id: 'sec-shield', d: dShield, node: shEnd, pulse: true },
    { id: 'sec-ledger', d: groundElbow(rightV(plate), leftV(ledger)), node: leftV(ledger), pulse: true },
    { id: 'sec-pin', d: groundElbow(leftV(plate), rightV(pin)), node: rightV(pin), pulse: true },
    { id: 'sec-tenantdb', d: groundElbow(topV(tenantdb), bottomV(plate)), node: topV(tenantdb), pulse: true },
  ];
})();

const OBJECTS: IsoObjectSpec[] = [
  { id: 'lock', element: <LockTile />, label: { x: 340, y: 30, text: 'TLS', lead: { y1: 38, y2: 50 } } },
  { id: 'shield', element: <ShieldTile />, label: { x: 910, y: 34, text: 'ABUSE CHECK', lead: { y1: 40, y2: 52 } } },
  { id: 'ledger', element: <LedgerTile />, label: { x: 1014, y: 428, text: 'REQUEST LOG', lead: { y1: 395, y2: 410 } } },
  { id: 'pin', element: <PinTile />, label: { x: 103, y: 450, text: 'IPS ANONYMISED', lead: { y1: 419, y2: 432 } } },
  { id: 'plate', element: <RouterPlate box={S.plate} /> },
  { id: 'tenantdb', element: <TenantDbTile />, label: { x: 590, y: 836, text: 'TENANT BOUNDARY', lead: { y1: 805, y2: 818 } } },
];

const MOTION: IsoMotion = {
  plate: { depth: 0.5, rise: 60 },
  lock: { depth: 1.7, rise: -120 }, // drops in from above
  shield: { depth: 1.5, rise: -110 }, // drops in from above
  ledger: { depth: 1.4, rise: 110 },
  pin: { depth: 1.5, rise: 115 },
  tenantdb: { depth: 1.2, rise: 105 },
};

/** Everything IsoSceneStage needs; the hero spreads this and adds styling. */
export const SECURITY_SCENE = {
  viewBox: '0 0 1160 860',
  coreId: 'plate',
  objects: OBJECTS,
  connectors: CONNECTORS,
  motion: MOTION,
  shadows: [
    { box: S.plate },
    { box: S.lock, rx: 9 },
    { box: S.shield, rx: 9 },
    { box: S.ledger, rx: 9 },
    { box: S.pin, rx: 9 },
    { box: S.tenantdb, rx: 9 },
  ],
};
